import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Eye, Loader2, Search, X, ShoppingBag, IndianRupee, TrendingUp, Calendar, Download, History, User as UserIcon, Lock, Wallet, Receipt,
  TicketPercent, TicketX, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n || 0));

// ── Coupon breakdown (order details popup) ──────────────────────────────
// item.coupon from GET /api/orders/:orderId:
//   { applied: true | false | null, discount, couponAllowed, reason }
//   applied === null → the order had no coupon.
const ItemCouponBadge = ({ coupon }) => {
  if (!coupon) return null;

  // Got the coupon, but coupons are disabled for the item now (setting
  // changed after the order, or the order went through an API without
  // item-wise coupon rules). The discount was really given, so show it —
  // flagged, with the reason.
  if (coupon.applied === true && coupon.disabledAfterOrder) {
    return (
      <div className="mt-1 text-xs" data-testid="item-coupon-applied-now-disabled">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-1.5 py-0.5 font-medium text-orange-800">
            <TicketPercent size={12} /> Coupon applied · disabled now
          </span>
          {Number(coupon.discount) > 0 && (
            <span className="text-orange-700">-₹{formatINR(coupon.discount)} off this item</span>
          )}
        </div>
        {coupon.note && <p className="mt-0.5 text-[11px] text-orange-700">{coupon.note}</p>}
      </div>
    );
  }

  if (coupon.applied === true) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid="item-coupon-applied">
        <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">
          <TicketPercent size={12} /> Coupon applied
        </span>
        {Number(coupon.discount) > 0 && (
          <span className="text-green-700">-₹{formatINR(coupon.discount)} off this item</span>
        )}
      </div>
    );
  }

  if (coupon.applied === false) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid="item-coupon-not-applied">
        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
          <TicketX size={12} /> Coupon not applied
        </span>
        {coupon.reason && <span className="text-amber-700">{coupon.reason}</span>}
      </div>
    );
  }

  // No coupon on the order — only note items that had coupons switched off.
  if (coupon.couponAllowed === false) {
    return (
      <div className="mt-1 text-xs text-gray-500" data-testid="item-coupon-disabled">
        Coupons disabled for this item
      </div>
    );
  }
  return null;
};

const CouponSummaryCard = ({ coupon }) => {
  const heading = coupon.code || coupon.title || 'Offer';
  const showTitle = coupon.code && coupon.title;
  return (
    <div
      className="mt-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3 text-sm"
      data-testid="order-coupon-info"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Receipt size={14} className="text-orange-700" />
            <span className="font-semibold text-orange-800 font-mono">{heading}</span>
            {showTitle && <span className="text-xs text-orange-700">· {coupon.title}</span>}
          </div>
          {coupon.description && (
            <div className="mt-0.5 text-xs text-orange-700">
              {coupon.description}
              {Number(coupon.minOrder) > 0 && ` · min order ₹${formatINR(coupon.minOrder)}`}
            </div>
          )}
        </div>
        <span className="whitespace-nowrap font-semibold text-orange-800">
          -₹{formatINR(coupon.amount)}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
        <div className="text-green-800">
          Applied on {coupon.appliedItemsCount} item{coupon.appliedItemsCount === 1 ? '' : 's'}
          {coupon.eligibleAmount != null && <> · ₹{formatINR(coupon.eligibleAmount)}</>}
        </div>
        {coupon.notAppliedItemsCount > 0 && (
          <div className="text-amber-800" data-testid="order-coupon-excluded">
            Not applied on {coupon.notAppliedItemsCount} item{coupon.notAppliedItemsCount === 1 ? '' : 's'}
            {' '}· ₹{formatINR(coupon.excludedAmount)}
          </div>
        )}
      </div>

      {coupon.source === 'LEGACY' && (
        <p className="mt-2 text-[11px] text-gray-500">
          This order has no item-wise coupon record (placed through an API version without item-wise coupon
          rules), so the coupon applied to every item; per-item amounts are estimated.
        </p>
      )}
      {coupon.disabledAfterOrderCount > 0 && (
        <p className="mt-1 text-[11px] font-medium text-orange-700" data-testid="order-coupon-disabled-after">
          {coupon.disabledAfterOrderCount} item{coupon.disabledAfterOrderCount === 1 ? ' has' : 's have'} coupons
          disabled now but received this discount when the order was placed.
        </p>
      )}
    </div>
  );
};

// Delivery address is stored as a free-form snapshot of the customer's saved
// address (flatNo, buildingName, landmark, area, state, pincode, geoLocation...).
// geoLocation is usually the full human-readable address text; fall back to
// composing the parts if it's missing.
const formatDeliveryAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  if (addr.geoLocation) return addr.geoLocation;
  const parts = [addr.flatNo, addr.buildingName, addr.landmark, addr.area, addr.state, addr.pincode]
    .filter(Boolean);
  return parts.join(', ');
};

// Orders placed before the payment-method normalization fix may still have
// the legacy "RAZORPAY" value stored — map that (and "ONLINE") to the same
// friendly "Prepaid" label so old and new orders read consistently.
const paymentMethodLabel = (method) => {
  if (method === 'COD') return 'COD';
  if (method === 'ONLINE' || method === 'RAZORPAY') return 'Prepaid';
  return method || '—';
};

const RESTAURANT_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  ACCEPTED: 'bg-blue-100 text-blue-700 border-blue-200',
  PREPARING: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  PREPARING_COMPLETED: 'bg-teal-100 text-teal-700 border-teal-200',
  OUT_FOR_DELIVERY: 'bg-purple-100 text-purple-700 border-purple-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200'
};

const DELIVERY_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  // Admin has assigned a rider but they haven't accepted/rejected the ring
  // yet (foodk_partner's Accept/Reject popup) — distinct from ACCEPTED so
  // it's obvious at a glance this is still pending the rider's response.
  ASSIGNED: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  ACCEPTED: 'bg-blue-100 text-blue-700 border-blue-200',
  OUT_FOR_DELIVERY: 'bg-purple-100 text-purple-700 border-purple-200',
  ARRIVED: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  DELIVERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
  // Log-only labels (never a live order.deliveryStatus value) — used purely
  // to color the History timeline entry a rider's response / an admin
  // unassign leaves behind.
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  UNASSIGNED: 'bg-slate-100 text-slate-600 border-slate-200'
};

// The manual "Change to" dropdown in the Order Status panel intentionally
// excludes ASSIGNED/REJECTED/UNASSIGNED — those are driven by the
// assign-agent flow and the rider's own Accept/Reject response, never by an
// admin picking a status from this list.
// Admin may only move a delivery to Out for Delivery or Delivered — every
// other delivery status is driven by the rider app.
const DELIVERY_STATUS_DROPDOWN_OPTIONS = ['OUT_FOR_DELIVERY', 'DELIVERED'];
const DELIVERY_STATUS_LABELS = {
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered'
};

// Admin may only move the restaurant side to these statuses — the rest are
// driven by the restaurant app / order flow.
const RESTAURANT_STATUS_DROPDOWN_OPTIONS = ['PREPARING', 'PREPARING_COMPLETED', 'CANCELLED'];
const RESTAURANT_STATUS_LABELS = {
  PREPARING: 'Preparing',
  PREPARING_COMPLETED: 'Preparing Completed',
  CANCELLED: 'Cancelled'
};

// Legacy alias used in the orders table column
const STATUS_COLORS = RESTAURANT_STATUS_COLORS;

const PAYMENT_STATUS_COLORS = {
  PAID: 'bg-green-100 text-green-700 border-green-200',
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200'
};

const colorForLog = (log) =>
  log?.type === 'delivery'
    ? DELIVERY_STATUS_COLORS[log.to] || 'bg-slate-100 text-slate-700'
    : RESTAURANT_STATUS_COLORS[log?.to] || 'bg-slate-100 text-slate-700';

// Local-day helpers
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const todayStart       = () => startOfDay(new Date());
const todayEnd         = () => endOfDay(new Date());
const yesterdayStart   = () => { const x = todayStart(); x.setDate(x.getDate() - 1); return x; };
const yesterdayEnd     = () => { const x = todayEnd();   x.setDate(x.getDate() - 1); return x; };
const monthStart       = () => { const x = todayStart(); x.setDate(1); return x; };
const monthEnd         = () => todayEnd();
const sevenDaysStart   = () => { const x = todayStart(); x.setDate(x.getDate() - 6); return x; };
const toInputDate      = (d) => format(d, 'yyyy-MM-dd');

const RANGES = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7',     label: 'Last 7 days' },
  { id: 'month',     label: 'This Month' },
  { id: 'all',       label: 'All time' },
  { id: 'custom',    label: 'Custom' }
];

// Fallback for a backend that still returns the full order array from
// GET /orders (pre-pagination deploy): filter/paginate/summarise client-side
// so the page keeps working until the new backend is live.
const processLegacyOrders = (all, { page, pageSize, statusFilter, restaurantFilter, search, dateWindow }) => {
  const q = (search || '').toLowerCase();
  const filtered = all.filter((o) => {
    if (dateWindow.from && new Date(o.createdAt) < dateWindow.from) return false;
    if (dateWindow.to && new Date(o.createdAt) > dateWindow.to) return false;
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (restaurantFilter !== 'all' && String(o.resturant?._id || o.resturantId) !== restaurantFilter) return false;
    if (q) {
      const hay = [o.orderNumber, o.user?.name, o.user?.phone, o.user?.email, o.resturant?.name, o.couponCode]
        .filter(Boolean).map((v) => String(v).toLowerCase());
      if (!hay.some((v) => v.includes(q))) return false;
    }
    return true;
  });
  let revenue = 0; let validCount = 0; let orderAmount = 0; let commission = 0;
  for (const o of filtered) {
    if (o.status === 'CANCELLED') continue;
    revenue += Number(o.grandTotal || 0); validCount++;
    if (!o.isDeleted) { orderAmount += Number(o.itemsTotal || 0); commission += Number(o.totalCommission || 0); }
  }
  const commissionPercent = orderAmount > 0 ? Number(((commission / orderAmount) * 100).toFixed(2)) : 0;

  const ranges = {
    today: [todayStart().getTime(), todayEnd().getTime()],
    yesterday: [yesterdayStart().getTime(), yesterdayEnd().getTime()],
    month: [monthStart().getTime(), monthEnd().getTime()]
  };
  const blank = () => ({ count: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 });
  const stats = { today: blank(), yesterday: blank(), month: blank(), all: blank() };
  const rmap = new Map();
  for (const o of all) {
    const r = o.resturant;
    if (r && r._id && r.name && !rmap.has(String(r._id))) rmap.set(String(r._id), r.name);
    if (o.status === 'CANCELLED' || o.isDeleted) continue;
    const t = new Date(o.createdAt).getTime();
    const tot = Number(o.grandTotal || 0);
    const amt = Number(o.itemsTotal || 0);
    const com = Number(o.totalCommission || 0);
    stats.all.count++; stats.all.revenue += tot; stats.all.orderAmount += amt; stats.all.commission += com;
    for (const k of Object.keys(ranges)) {
      if (t >= ranges[k][0] && t <= ranges[k][1]) {
        stats[k].count++; stats[k].revenue += tot; stats[k].orderAmount += amt; stats[k].commission += com;
      }
    }
  }
  // Overall % = total commission ÷ total order amount (not an average of per-order %).
  for (const k of Object.keys(stats)) {
    stats[k].commissionPercent = stats[k].orderAmount > 0
      ? Number(((stats[k].commission / stats[k].orderAmount) * 100).toFixed(2)) : 0;
  }

  return {
    orders: filtered.slice((page - 1) * pageSize, page * pageSize),
    total: filtered.length,
    summary: { count: filtered.length, validCount, revenue, orderAmount, commission, commissionPercent },
    stats,
    restaurants: Array.from(rmap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  };
};

const Orders = () => {
  // Permission flags. Roles limited to "today" (Order Manager) only see today's
  // numbers; the backend enforces this too, this just hides the empty cards.
  const { admin, can } = useAuth();
  const todayOnly = !admin?.isSuperAdmin && admin?.dashboardScope === 'today';
  const canEditOrders = can('orders', 'edit');
  // Send the customer a push + in-app notification when a status changes
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [orders, setOrders] = useState([]);           // current page only
  const [total, setTotal] = useState(0);                // matching orders (all pages)
  const [filteredSummary, setFilteredSummary] = useState({ count: 0, validCount: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 });
  const [stats, setStats] = useState({
    today: { count: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 },
    yesterday: { count: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 },
    month: { count: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 },
    all: { count: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 }
  });
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [loading, setLoading] = useState(true);         // first load → full-page spinner
  const [listLoading, setListLoading] = useState(false); // page/filter change → dim table
  const listReqId = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  const [range, setRange] = useState(todayOnly ? 'today' : 'all');
  const [customFrom, setCustomFrom] = useState(toInputDate(new Date()));
  const [customTo, setCustomTo] = useState(toInputDate(new Date()));

  // Pagination
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);

  // Details modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Delivery agents (for manual assignment)
  const [agents, setAgents] = useState([]);
  const [agentSelect, setAgentSelect] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Status update form (separate for restaurant + delivery)
  const [rStatusSelect, setRStatusSelect] = useState('');
  const [rRemarks, setRRemarks] = useState('');
  const [updatingR, setUpdatingR] = useState(false);

  const [dStatusSelect, setDStatusSelect] = useState('');
  const [dRemarks, setDRemarks] = useState('');
  const [updatingD, setUpdatingD] = useState(false);

  useEffect(() => { fetchSummary(); fetchAgents(); }, []);

  // Riders go online/offline all the time — refresh the list every time an
  // order popup is opened so the "online only" dropdown isn't stale.
  useEffect(() => { if (selectedOrder) fetchAgents(); }, [selectedOrder]);

  // Auto-open the details modal when navigating in with ?open=<orderId>
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      fetchOrderDetails(openId);
      // strip the param so refresh doesn't keep reopening
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, []); // run once on mount

  // While an order sits in "ASSIGNED" (rung the rider, awaiting their
  // Accept/Reject — see assignDeliveryAgent / respondToAssignment), lightly
  // poll its details so admin sees the response — and the History entry it
  // creates — land in this modal on its own, without closing and reopening
  // it. Self-terminating: the moment deliveryStatus moves off ASSIGNED
  // (accepted, rejected, or reassigned elsewhere) this effect's own
  // dependency changes, its cleanup clears the interval, and — since the
  // new status isn't ASSIGNED — no replacement interval is scheduled.
  useEffect(() => {
    if (!selectedOrder || orderDetails?.order?.deliveryStatus !== 'ASSIGNED') {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/orders/${selectedOrder}`);
        const fresh = res.data?.order;
        if (!fresh) return;
        setOrderDetails((prev) =>
          prev && prev.order
            ? {
                ...prev,
                order: {
                  ...prev.order,
                  deliveryStatus: fresh.deliveryStatus,
                  deliveryAgent: fresh.deliveryAgent,
                  statusLogs: fresh.statusLogs,
                  settledAt: fresh.settledAt,
                },
              }
            : prev
        );
        setOrders((prevList) =>
          prevList.map((o) =>
            o._id === selectedOrder ? { ...o, deliveryStatus: fresh.deliveryStatus } : o
          )
        );
      } catch (err) {
        // Silent — a transient poll failure shouldn't surface an error
        // toast; the next tick (or a manual reopen) picks it back up.
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedOrder, orderDetails?.order?.deliveryStatus]);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/delivery-agents`);
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data || res.data?.agents || [];
      setAgents(
        data
          .filter((a) => a && !a.isDeleted)
          .map((a) => ({
            id: a.id || a._id,
            name: a.name || 'Unnamed',
            phone: a.phone || '',
            // workStatus is 1/0 in the admin model, true/false from the app
            online: (a.workStatus === 1 || a.workStatus === true) && a.status !== false,
          }))
      );
    } catch (err) {
      console.error('Failed to fetch delivery agents:', err);
    }
  };

  const [exporting, setExporting] = useState(false);

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const p = {};
      if (statusFilter !== 'all') p.status = statusFilter;
      if (restaurantFilter !== 'all') p.restaurantId = restaurantFilter;
      if (search.trim()) p.search = search.trim();
      if (range !== 'all' && dateWindow?.from && dateWindow?.to) {
        p.from = dateWindow.from.toISOString();
        p.to = dateWindow.to.toISOString();
      }
      const res = await axios.get(`${API}/orders/export`, {
        params: p,
        responseType: 'blob'
      });
      const blob = new Blob([res.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Stat cards + restaurant dropdown — computed server-side, loaded once
  // (and on Refresh) rather than derived from every order in the browser.
  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/orders/summary`, {
        params: {
          todayStart: todayStart().toISOString(),
          todayEnd: todayEnd().toISOString(),
          yesterdayStart: yesterdayStart().toISOString(),
          yesterdayEnd: yesterdayEnd().toISOString(),
          monthStart: monthStart().toISOString()
        }
      });
      if (res.data?.stats) setStats((prev) => ({ ...prev, ...res.data.stats }));
      if (Array.isArray(res.data?.restaurants)) setRestaurantOptions(res.data.restaurants);
    } catch (error) {
      console.error('Summary API Error:', error?.response?.data || error.message);
    }
  };

  // One page of orders, filtered + paginated on the server.
  const fetchOrders = async () => {
    const reqId = ++listReqId.current;
    try {
      setListLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (restaurantFilter !== 'all') params.restaurantId = restaurantFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateWindow.from) params.from = dateWindow.from.toISOString();
      if (dateWindow.to) params.to = dateWindow.to.toISOString();
      const res = await axios.get(`${API}/orders`, { params });
      if (reqId !== listReqId.current) return; // a newer request superseded this one
      if (Array.isArray(res.data)) {
        // Old backend (no server-side pagination yet) — handle it here.
        const legacy = processLegacyOrders(res.data, {
          page, pageSize: PAGE_SIZE, statusFilter, restaurantFilter, search: debouncedSearch, dateWindow
        });
        setOrders(legacy.orders);
        setTotal(legacy.total);
        setFilteredSummary(legacy.summary);
        setStats(legacy.stats);
        setRestaurantOptions(legacy.restaurants);
        return;
      }
      setOrders(res.data?.orders || []);
      setTotal(res.data?.total || 0);
      setFilteredSummary(res.data?.summary || { count: 0, validCount: 0, revenue: 0, orderAmount: 0, commission: 0, commissionPercent: 0 });
    } catch (error) {
      if (reqId !== listReqId.current) return;
      console.error('API Error:', error?.response?.data || error.message);
      toast.error('Failed to fetch orders');
    } finally {
      if (reqId === listReqId.current) {
        setListLoading(false);
        setLoading(false);
      }
    }
  };

  const refreshAll = () => { fetchOrders(); fetchSummary(); };

  const fetchOrderDetails = async (orderId) => {
    try {
      setDetailsLoading(true);
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrderDetails(res.data);
      setSelectedOrder(orderId);
      setAgentSelect(res.data?.order?.deliveryAgent?.id || '');
      // Only preselect the current status if it's one of the allowed options.
      const fetchedRestaurantStatus = res.data?.order?.status || '';
      setRStatusSelect(
        RESTAURANT_STATUS_DROPDOWN_OPTIONS.includes(fetchedRestaurantStatus)
          ? fetchedRestaurantStatus
          : ''
      );
      // ASSIGNED (pending rider acceptance) isn't one of the manual dropdown
      // options (see DELIVERY_STATUS_DROPDOWN_OPTIONS) — default the "Change
      // to" select to blank rather than a value it can't actually display,
      // so the dropdown itself never shows a phantom selection.
      const fetchedDeliveryStatus = res.data?.order?.deliveryStatus || '';
      setDStatusSelect(
        DELIVERY_STATUS_DROPDOWN_OPTIONS.includes(fetchedDeliveryStatus)
          ? fetchedDeliveryStatus
          : ''
      );
      setRRemarks('');
      setDRemarks('');
    } catch (err) {
      toast.error('Failed to fetch order details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const updateStatus = async (type) => {
    if (!selectedOrder) return;
    const isDelivery = type === 'delivery';
    const newStatus  = isDelivery ? dStatusSelect : rStatusSelect;
    const remarks    = isDelivery ? dRemarks     : rRemarks;
    const current    = isDelivery
      ? (orderDetails?.order?.deliveryStatus || '')
      : (orderDetails?.order?.status || '');

    if (!newStatus || newStatus === current) {
      toast.error('Please choose a different status');
      return;
    }
    if (!remarks.trim()) {
      toast.error('Please add a remark for this status change');
      return;
    }
    try {
      isDelivery ? setUpdatingD(true) : setUpdatingR(true);
      const res = await axios.patch(
        `${API}/orders/${selectedOrder}/status`,
        { type, status: newStatus, remarks: remarks.trim(), notifyUser: notifyCustomer }
      );
      const newLog = res.data?.order?.statusLog;
      // Marking delivery DELIVERED settles the order server-side — for a
      // COD order that also flips paymentStatus to PAID (see
      // orders.controller.js). The response already reflects this
      // (res.data.order.status/deliveryStatus/settledAt), so fold it in
      // locally rather than only trusting the status we optimistically
      // guessed — otherwise "Paid"/"Settled" only ever showed up after a
      // manual reopen of this modal.
      const isCodDelivered =
        isDelivery && newStatus === 'DELIVERED' && orderDetails?.order?.paymentMethod === 'COD';
      setOrderDetails((prev) =>
        prev
          ? {
              ...prev,
              order: {
                ...prev.order,
                ...(isDelivery
                  ? { deliveryStatus: newStatus }
                  : { status: newStatus }),
                ...(isCodDelivered ? { paymentStatus: 'PAID' } : {}),
                settledAt: res.data?.order?.settledAt ?? prev.order.settledAt,
                statusLogs: newLog ? [newLog, ...(prev.order.statusLogs || [])] : prev.order.statusLogs
              }
            }
          : prev
      );
      // The orders TABLE (as opposed to this details modal) previously only
      // got patched for restaurant-status changes — a delivery-status
      // change (e.g. marking DELIVERED) left the table's Restaurant
      // Status/Payment Status badges stale until the page was reloaded.
      setOrders((prev) =>
        prev.map((o) =>
          o._id === selectedOrder
            ? {
                ...o,
                ...(isDelivery ? { deliveryStatus: newStatus } : { status: newStatus }),
                ...(isCodDelivered ? { paymentStatus: 'PAID' } : {})
              }
            : o
        )
      );
      isDelivery ? setDRemarks('') : setRRemarks('');
      toast.success(res.data?.message || 'Status updated');
      const cn = res.data?.order?.customerNotification;
      if (cn?.sent) toast.success('Customer notified');
      else if (cn?.saved) toast.info('Customer notification saved in-app (no device token for push)');
      else if (cn?.reason === 'push_failed' || cn?.reason === 'timeout' || cn?.reason === 'error') {
        toast.warning('Status updated, but the customer notification could not be sent');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      isDelivery ? setUpdatingD(false) : setUpdatingR(false);
    }
  };

  const assignAgent = async () => {
    if (!selectedOrder) return;
    try {
      setAssigning(true);
      const body = { deliveryAgentId: agentSelect || null };
      const res = await axios.patch(
        `${API}/orders/${selectedOrder}/assign-agent`,
        body
      );
      const newDeliveryStatus = res.data?.deliveryStatus ?? (res.data?.deliveryAgent ? 'ASSIGNED' : null);
      const newLog = res.data?.statusLog;
      // Refresh order details modal — including deliveryStatus (now
      // "ASSIGNED", not "ACCEPTED"; the order isn't really the rider's
      // until they accept the ring — see respondToAssignment in foodk_api)
      // and the History timeline, so admin sees this assignment logged
      // immediately without having to close and reopen the modal.
      setOrderDetails((prev) =>
        prev
          ? {
              ...prev,
              order: {
                ...prev.order,
                deliveryAgent: res.data?.deliveryAgent || null,
                deliveryStatus: newDeliveryStatus,
                statusLogs: newLog ? [newLog, ...(prev.order.statusLogs || [])] : prev.order.statusLogs,
              },
            }
          : prev
      );
      // Reset the manual "Change to" select to match the fresh status so it
      // doesn't keep showing a stale value from before the assignment.
      setDStatusSelect(
        DELIVERY_STATUS_DROPDOWN_OPTIONS.includes(newDeliveryStatus) ? newDeliveryStatus : ''
      );
      // Also refresh the underlying orders list so the row reflects the new agent
      setOrders((prev) =>
        prev.map((o) =>
          o._id === selectedOrder
            ? { ...o, deliveryAgentId: res.data?.deliveryAgent?.id || null, deliveryStatus: newDeliveryStatus }
            : o
        )
      );
      toast.success(res.data?.message || 'Updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign agent');
    } finally {
      setAssigning(false);
    }
  };

  // ---- Resolved date window for the active range ----
  const dateWindow = useMemo(() => {
    switch (range) {
      case 'today':     return { from: todayStart(),     to: todayEnd() };
      case 'yesterday': return { from: yesterdayStart(), to: yesterdayEnd() };
      case 'last7':     return { from: sevenDaysStart(), to: todayEnd() };
      case 'month':     return { from: monthStart(),     to: monthEnd() };
      case 'custom': {
        const f = customFrom ? startOfDay(new Date(customFrom)) : null;
        const t = customTo ? endOfDay(new Date(customTo)) : null;
        return { from: f, to: t };
      }
      default: return { from: null, to: null };
    }
  }, [range, customFrom, customTo]);

  // Debounce typing in the search box so we don't hit the API per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, restaurantFilter, range, customFrom, customTo]);

  // Fetch the current page whenever page or filters change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, statusFilter, restaurantFilter, dateWindow]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedOrders = orders;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-sm text-muted-foreground">Loading orders...</p>
      </div>
    );
  }

  const allStatCards = [
    { id: 'today',     label: "Today's Sales",      value: stats.today,     icon: ShoppingBag,  color: '#C2410C', bg: '#FFF7ED' },
    { id: 'yesterday', label: "Yesterday's Sales",  value: stats.yesterday, icon: Calendar,     color: '#3B82F6', bg: '#EFF6FF' },
    { id: 'month',     label: "This Month's Sales", value: stats.month,     icon: TrendingUp,   color: '#059669', bg: '#ECFDF5' },
    { id: 'all',       label: 'All Time Revenue',   value: stats.all,       icon: IndianRupee,  color: '#7C3AED', bg: '#F5F3FF' }
  ];
  const statCards = todayOnly ? allStatCards.filter((c) => c.id === 'today') : allStatCards;

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      {/* HEADER */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            Orders
          </h1>
          <p className="text-gray-600">Manage incoming and past orders</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportToExcel}
            disabled={exporting || total === 0}
            data-testid="orders-export-button"
            size="sm"
            style={{ backgroundColor: '#C2410C' }}
            className="text-white"
          >
            {exporting ? (
              <Loader2 className="animate-spin mr-1" size={14} />
            ) : (
              <Download size={14} className="mr-1" />
            )}
            Export Excel
          </Button>
          <Button variant="outline" size="sm" onClick={refreshAll} data-testid="orders-refresh">
            Refresh
          </Button>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-testid="orders-stats">
        {statCards.map((c) => {
          const Icon = c.icon;
          const isActive = (c.id === 'today' && range === 'today')
            || (c.id === 'yesterday' && range === 'yesterday')
            || (c.id === 'month' && range === 'month')
            || (c.id === 'all' && range === 'all');
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setRange(c.id)}
              data-testid={`orders-stat-${c.id}`}
              className={`text-left bg-white rounded-xl border p-6 transition-all hover:shadow-md
                ${isActive ? 'border-orange-300 ring-2 ring-orange-200' : 'border-gray-200'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: c.bg }}>
                  <Icon size={22} style={{ color: c.color }} />
                </div>
                <span className="text-xs text-gray-500">{c.value.count} orders</span>
              </div>
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
                ₹{formatINR(c.value.revenue)}
              </h3>
              <p className="text-sm text-gray-600 mt-1">{c.label}</p>
              {!todayOnly && (
              <p
                className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100"
                data-testid={`orders-stat-${c.id}-commission`}
              >
                Commission ₹{formatINR(c.value.commission)} · {formatINR(c.value.commissionPercent)}%
              </p>
              )}
            </button>
          );
        })}
      </div>

      {/* COMMISSION SUMMARY — follows the active filters (date range, status,
          restaurant, search). Cancelled / refunded orders are excluded.
          Hidden for "today only" roles (Order Manager). */}
      {!todayOnly && (
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="orders-commission-summary">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Percent size={14} /> Commission Summary
          </h3>
          <span className="text-xs text-gray-500">
            Based on {filteredSummary.validCount ?? 0} valid orders in the current view (cancelled/refunded excluded)
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="commission-order-amount">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-800">Applicable Order Amount</p>
            <p className="text-xl font-bold text-blue-900">₹{formatINR(filteredSummary.orderAmount)}</p>
          </div>
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-3" data-testid="commission-total">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-800">Total Commission Collected</p>
            <p className="text-xl font-bold text-purple-900">₹{formatINR(filteredSummary.commission)}</p>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="commission-overall-percent">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">Overall Commission %</p>
            <p className="text-xl font-bold text-emerald-900">{formatINR(filteredSummary.commissionPercent)}%</p>
          </div>
        </div>
      </div>
      )}

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="orders-filter-bar">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="orders-search-input"
            placeholder="Search order #, customer name or phone..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              data-testid="orders-search-clear"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-[160px]" data-testid="orders-range-select">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.id} value={r.id} data-testid={`orders-range-${r.id}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {range === 'custom' && (
          <div className="flex items-center gap-2" data-testid="orders-custom-range">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="w-[150px]"
              data-testid="orders-custom-from"
            />
            <span className="text-gray-500 text-sm">to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="w-[150px]"
              data-testid="orders-custom-to"
            />
          </div>
        )}

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="orders-status-select">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_COLORS).map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
          <SelectTrigger className="w-[200px]" data-testid="orders-restaurant-select">
            <SelectValue placeholder="Restaurant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All restaurants</SelectItem>
            {restaurantOptions.map((r) => (
              <SelectItem key={r.id} value={r.id} data-testid={`orders-restaurant-${r.id}`}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(search || statusFilter !== 'all' || restaurantFilter !== 'all' || range !== 'all') && (
          <Button
            variant="outline"
            size="sm"
            data-testid="orders-clear-filters"
            onClick={() => {
              setSearch('');
              setStatusFilter('all');
              setRestaurantFilter('all');
              setRange('all');
            }}
          >
            Clear filters
          </Button>
        )}

        <div className="ml-auto text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{filteredSummary.count}</span> orders ·{' '}
          <span className="font-semibold text-gray-900">₹{formatINR(filteredSummary.revenue)}</span>
        </div>
      </div>

      {/* TABLE */}
      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="px-0">
          <div className={`rounded-lg border bg-white overflow-hidden transition-opacity ${listLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <Table data-testid="orders-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Order ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Restaurant Status</TableHead>
                  <TableHead>Delivery Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedOrders.length > 0 ? pagedOrders.map((order) => (
                  <TableRow key={order._id} data-testid={`order-row-${order._id}`}>
                    {/* Customer name, distance, delivery address, and coupon
                        used to be full columns here — that's a lot to render
                        (and scan) for every row when it's only actually
                        needed once, for the specific order someone opens via
                        Action. They now only show inside the Action/details
                        modal (see below); Order ID here also opens straight
                        into that same modal so getting to them is a single
                        click either way. */}
                    <TableCell className="font-bold text-primary">
                      <button
                        type="button"
                        className="hover:underline"
                        data-testid={`order-id-link-${order._id}`}
                        onClick={() => fetchOrderDetails(order._id)}
                      >
                        #{order.orderNumber}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs">
                      {format(new Date(order.createdAt), 'dd MMM, yyyy')}<br />
                      {format(new Date(order.createdAt), 'hh:mm a')}
                    </TableCell>
                    <TableCell>{order.user?.phone || '-'}</TableCell>
                    <TableCell className="text-sm">{order.resturant?.name || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{paymentMethodLabel(order.paymentMethod)}</Badge></TableCell>
                    <TableCell data-testid={`order-payment-status-${order._id}`}>
                      <Badge className={PAYMENT_STATUS_COLORS[order.paymentStatus] || 'bg-slate-100 text-slate-700 border-slate-200'}>
                        {order.paymentStatus || 'PENDING'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">₹{formatINR(order.grandTotal)}</TableCell>
                    <TableCell data-testid={`order-rest-status-${order._id}`}>
                      <Badge className={RESTAURANT_STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-700'}>
                        {order.status || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`order-del-status-${order._id}`}>
                      {order.deliveryStatus ? (
                        <Badge className={DELIVERY_STATUS_COLORS[order.deliveryStatus] || 'bg-slate-100 text-slate-700'}>
                          {order.deliveryStatus}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`view-order-${order._id}`}
                        onClick={() => fetchOrderDetails(order._id)}
                      >
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-gray-500">
                      No orders match your filters
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* PAGINATION */}
          {total > PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1" data-testid="orders-pagination">
              <p className="text-sm text-gray-600">
                Showing{' '}
                <span className="font-semibold text-gray-900">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)}
                </span>{' '}
                of <span className="font-semibold text-gray-900">{total}</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="orders-page-prev"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700 px-2" data-testid="orders-page-indicator">
                  Page <span className="font-semibold">{currentPage}</span> / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="orders-page-next"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ORDER DETAILS MODAL */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="relative bg-white w-[760px] max-w-[95vw] max-h-[85vh] overflow-y-auto rounded-lg p-5">
            {/* Quick-close — previously the only way out was the "Close"
                button at the very bottom, which meant scrolling all the way
                down through a long order (items, delivery, status history,
                money flow...) just to dismiss it. */}
            <button
              type="button"
              aria-label="Close"
              data-testid="order-details-close"
              onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}
              className="absolute top-3 right-3 z-10 rounded-full p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>

            {detailsLoading ? (
              <Loader2 className="animate-spin mx-auto" />
            ) : (
              <>
                <h2 className="text-lg font-bold mb-3 pr-8">
                  Order #{orderDetails?.order?.orderNumber}
                </h2>
                <p className="text-sm">{orderDetails?.order?.user?.name}</p>
                <p className="text-sm mb-3">{orderDetails?.order?.user?.phone}</p>

                {/* ADDRESSES */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid="order-addresses">
                  <div className="rounded-md border border-slate-200 bg-slate-50/70 p-2.5">
                    <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-1">
                      Restaurant Address
                    </p>
                    {orderDetails?.order?.resturant?.name && (
                      <p className="text-sm font-semibold text-gray-900">
                        {orderDetails.order.resturant.name}
                      </p>
                    )}
                    <p className="text-sm text-gray-700">
                      {orderDetails?.order?.resturant?.address || 'Not available'}
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-testid="order-restaurant-phone">
                      <span className="text-slate-500">Phone: </span>
                      {orderDetails?.order?.resturant?.phone ? (
                        <a
                          href={`tel:${orderDetails.order.resturant.phone}`}
                          className="font-medium text-blue-700 hover:underline"
                        >
                          {orderDetails.order.resturant.phone}
                        </a>
                      ) : (
                        'Not available'
                      )}
                    </p>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50/70 p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                        Delivery Address
                      </p>
                      {orderDetails?.order?.distanceKm != null && (
                        <span
                          className="text-[11px] font-semibold text-slate-600"
                          data-testid="order-distance"
                        >
                          {orderDetails.order.distanceKm} km
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">
                      {formatDeliveryAddress(orderDetails?.order?.deliveryAddress) || 'Not available'}
                    </p>
                    {(orderDetails?.order?.deliveryAddress?.contactName || orderDetails?.order?.deliveryAddress?.contactPhone) && (
                      <p className="text-xs text-gray-500 mt-1">
                        {orderDetails.order.deliveryAddress.contactName}
                        {orderDetails.order.deliveryAddress.contactName && orderDetails.order.deliveryAddress.contactPhone ? ' · ' : ''}
                        {orderDetails.order.deliveryAddress.contactPhone}
                      </p>
                    )}
                  </div>
                </div>

                {orderDetails?.order?.items?.length > 0 ? (
                  orderDetails.order.items.map((item, index) => (
                    <div key={index} className="border p-2 mb-2 rounded">
                      <div className="flex justify-between font-medium">
                        <span>{item.name} × {item.quantity}</span>
                        <span>₹{item.totalPrice}</span>
                      </div>
                      <ItemCouponBadge coupon={item.coupon} />
                      {item.basePrice != null && (
                        <div className="text-xs text-gray-500">
                          Base price: ₹{item.basePrice} / unit
                        </div>
                      )}
                      {item.addons?.length > 0 && (
                        <div className="ml-3 text-xs text-gray-500">
                          {item.addons.map((addon, i) => (
                            <div key={i}>+ {addon.title} (₹{addon.price})</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">No items found</div>
                )}

                {/* DELIVERY INFO */}
                <div
                  className="mt-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3"
                  data-testid="order-delivery-info"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-orange-800">
                      Delivery
                    </h3>
                    <span className="text-xs text-orange-700">
                      ETA:{' '}
                      <span className="font-semibold">
                        {orderDetails?.order?.deliveryTime
                          ? `${orderDetails.order.deliveryTime} min`
                          : '—'}
                      </span>
                    </span>
                  </div>

                  {orderDetails?.order?.deliveryAgent ? (
                    <div
                      className="flex items-center gap-3 mb-3"
                      data-testid="order-assigned-agent"
                    >
                      {orderDetails.order.deliveryAgent.image ? (
                        <img
                          src={orderDetails.order.deliveryAgent.image}
                          alt={orderDetails.order.deliveryAgent.name}
                          className="w-10 h-10 rounded-full object-cover border border-orange-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center text-orange-800 font-bold text-sm">
                          {(orderDetails.order.deliveryAgent.name || '?')
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">
                          {orderDetails.order.deliveryAgent.name}
                        </div>
                        <div className="text-xs text-gray-600">
                          {orderDetails.order.deliveryAgent.phone}
                          {orderDetails.order.deliveryAgent.vehicleNo
                            ? ` • ${orderDetails.order.deliveryAgent.vehicleNo}`
                            : ''}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600 mb-3 italic">
                      No delivery agent assigned yet.
                    </div>
                  )}

                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Assign delivery agent
                  </label>
                  <div className="flex gap-2">
                    <select
                      data-testid="order-agent-select"
                      disabled={!canEditOrders}
                      value={agentSelect}
                      onChange={(e) => setAgentSelect(e.target.value)}
                      className="flex-1 text-sm rounded-md border border-gray-300 bg-white px-2 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="">— Unassigned —</option>
                      {agents
                        // Only online riders can be picked. The rider already
                        // on this order stays listed (marked Offline) so the
                        // select still shows the current assignment.
                        .filter((a) => a.online || a.id === orderDetails?.order?.deliveryAgent?.id)
                        .map((a) => (
                          <option key={a.id} value={a.id} disabled={!a.online}>
                            {a.name} {a.phone ? `(${a.phone})` : ''}{a.online ? '' : ' — Offline'}
                          </option>
                        ))}
                    </select>
                    <Button
                      type="button"
                      onClick={assignAgent}
                      disabled={
                        !canEditOrders ||
                        assigning ||
                        (agentSelect || '') ===
                          (orderDetails?.order?.deliveryAgent?.id || '')
                      }
                      data-testid="order-assign-agent-button"
                      style={{ backgroundColor: '#C2410C' }}
                      className="text-white text-sm"
                    >
                      {assigning ? 'Saving…' : 'Assign'}
                    </Button>
                  </div>
                  {agents.filter((a) => a.online).length === 0 && (
                    <p className="text-xs text-amber-700 mt-1" data-testid="order-no-online-agents">
                      No delivery agents are online right now.
                    </p>
                  )}
                </div>

                {/* STATUS UPDATE + HISTORY */}
                <div
                  className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3"
                  data-testid="order-status-section"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <History size={14} /> Order Status
                    </h3>
                    {orderDetails?.order?.settledAt && (
                      <Badge
                        className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] flex items-center gap-1"
                        data-testid="order-settled-badge"
                      >
                        <Lock size={10} /> Settled · locked
                      </Badge>
                    )}
                  </div>

                  {orderDetails?.order?.settledAt && (
                    <div
                      className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                      data-testid="order-settled-banner"
                    >
                      Order was settled on{' '}
                      <span className="font-semibold">
                        {format(new Date(orderDetails.order.settledAt), 'dd MMM yyyy, hh:mm a')}
                      </span>{' '}
                      — restaurant and delivery wallets have been credited and the
                      status can no longer be changed.
                    </div>
                  )}

                  <label className="flex items-center gap-2 text-xs text-gray-700 mb-2 select-none" data-testid="notify-customer-toggle">
                    <input
                      type="checkbox"
                      checked={notifyCustomer}
                      disabled={!canEditOrders}
                      onChange={(e) => setNotifyCustomer(e.target.checked)}
                    />
                    Notify the customer about status changes
                  </label>

                  {/* TWO STATUS FORMS — Restaurant & Delivery */}
                  <fieldset
                    disabled={!!orderDetails?.order?.settledAt || !canEditOrders}
                    className="grid grid-cols-1 md:grid-cols-2 gap-3 disabled:opacity-60"
                  >
                    {/* Restaurant */}
                    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid="status-form-restaurant">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                          Restaurant
                        </span>
                        <Badge className={RESTAURANT_STATUS_COLORS[orderDetails?.order?.status] || 'bg-slate-100 text-slate-700'}>
                          {orderDetails?.order?.status || '—'}
                        </Badge>
                      </div>
                      <label className="block text-[11px] font-medium text-gray-700 mb-1">Change to</label>
                      <select
                        data-testid="restaurant-status-select"
                        value={rStatusSelect}
                        onChange={(e) => setRStatusSelect(e.target.value)}
                        className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      >
                        <option value="" disabled>— Select status —</option>
                        {RESTAURANT_STATUS_DROPDOWN_OPTIONS.map((s) => (
                          <option key={s} value={s}>{RESTAURANT_STATUS_LABELS[s] || s}</option>
                        ))}
                      </select>
                      <textarea
                        data-testid="restaurant-status-remarks"
                        value={rRemarks}
                        onChange={(e) => setRRemarks(e.target.value)}
                        rows={2}
                        placeholder="Remarks (required)"
                        className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                      />
                      <Button
                        type="button"
                        onClick={() => updateStatus('restaurant')}
                        disabled={
                          updatingR ||
                          !rStatusSelect ||
                          rStatusSelect === orderDetails?.order?.status ||
                          !rRemarks.trim()
                        }
                        data-testid="update-restaurant-status-button"
                        style={{ backgroundColor: '#C2410C' }}
                        className="text-white text-xs w-full h-8"
                      >
                        {updatingR ? 'Updating…' : 'Update Restaurant Status'}
                      </Button>
                    </div>

                    {/* Delivery */}
                    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid="status-form-delivery">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                          Delivery Agent
                        </span>
                        <Badge className={DELIVERY_STATUS_COLORS[orderDetails?.order?.deliveryStatus] || 'bg-slate-100 text-slate-700'}>
                          {orderDetails?.order?.deliveryStatus || 'NOT SET'}
                        </Badge>
                      </div>
                      <label className="block text-[11px] font-medium text-gray-700 mb-1">Change to</label>
                      <select
                        data-testid="delivery-status-select"
                        value={dStatusSelect}
                        onChange={(e) => setDStatusSelect(e.target.value)}
                        className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      >
                        <option value="" disabled>— Select status —</option>
                        {DELIVERY_STATUS_DROPDOWN_OPTIONS.map((s) => (
                          <option key={s} value={s}>{DELIVERY_STATUS_LABELS[s] || s}</option>
                        ))}
                      </select>
                      <textarea
                        data-testid="delivery-status-remarks"
                        value={dRemarks}
                        onChange={(e) => setDRemarks(e.target.value)}
                        rows={2}
                        placeholder="Remarks (required)"
                        className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                      />
                      <Button
                        type="button"
                        onClick={() => updateStatus('delivery')}
                        disabled={
                          updatingD ||
                          !dStatusSelect ||
                          dStatusSelect === (orderDetails?.order?.deliveryStatus || '') ||
                          !dRemarks.trim()
                        }
                        data-testid="update-delivery-status-button"
                        style={{ backgroundColor: '#0E7490' }}
                        className="text-white text-xs w-full h-8"
                      >
                        {updatingD ? 'Updating…' : 'Update Delivery Status'}
                      </Button>
                    </div>
                  </fieldset>

                  {/* COMBINED HISTORY */}
                  <div className="mt-3" data-testid="order-status-history">
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                      History ({orderDetails?.order?.statusLogs?.length || 0})
                    </p>
                    {orderDetails?.order?.statusLogs?.length ? (
                      <ol className="relative border-l border-slate-300 ml-2 space-y-3 max-h-56 overflow-y-auto pr-1">
                        {orderDetails.order.statusLogs.map((log, idx) => {
                          const isDel = log.type === 'delivery';
                          const fromColor = isDel
                            ? DELIVERY_STATUS_COLORS[log.from] || 'bg-slate-100 text-slate-700'
                            : RESTAURANT_STATUS_COLORS[log.from] || 'bg-slate-100 text-slate-700';
                          const toColor = colorForLog(log);
                          return (
                            <li
                              key={idx}
                              className="ml-3"
                              data-testid={`order-status-log-${idx}`}
                            >
                              <span className="absolute -left-1.5 w-3 h-3 rounded-full border border-white bg-slate-400" />
                              <div className="text-xs flex items-center gap-1.5 flex-wrap">
                                <Badge
                                  className={`text-[10px] ${
                                    isDel
                                      ? 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                                      : 'bg-orange-50 text-orange-700 border border-orange-200'
                                  }`}
                                >
                                  {isDel ? 'Delivery' : 'Restaurant'}
                                </Badge>
                                {log.from && (
                                  <Badge className={`${fromColor} text-[10px]`}>
                                    {log.from}
                                  </Badge>
                                )}
                                {log.from && <span className="text-gray-400">→</span>}
                                <Badge className={`${toColor} text-[10px]`}>
                                  {log.to}
                                </Badge>
                                <span className="text-[11px] text-gray-500 ml-auto">
                                  {log.updatedAt
                                    ? format(new Date(log.updatedAt), 'dd MMM yyyy, hh:mm a')
                                    : ''}
                                </span>
                              </div>
                              {log.remarks && (
                                <p className="text-[12px] text-gray-700 mt-1 bg-white border border-slate-200 rounded px-2 py-1">
                                  {log.remarks}
                                </p>
                              )}
                              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                                <UserIcon size={11} />
                                <span className="font-medium text-gray-700">
                                  {log.updatedBy?.name || 'System'}
                                </span>
                                {log.updatedBy?.email && (
                                  <span className="text-gray-400">· {log.updatedBy.email}</span>
                                )}
                              </p>
                            </li>
                          );
                        })}
                      </ol>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No status changes recorded yet.</p>
                    )}
                  </div>
                </div>

                {orderDetails?.order?.coupon && (
                  <CouponSummaryCard coupon={orderDetails.order.coupon} />
                )}

                <div className="border-t pt-3 mt-3 text-sm space-y-1">
                  <div className="flex justify-between"><span>Items Total</span><span>₹{orderDetails?.order?.billSummary?.itemTotal}</span></div>
                  <div className="flex justify-between"><span>Tax</span><span>₹{orderDetails?.order?.billSummary?.taxAmount}</span></div>
                  <div className="flex justify-between"><span>Delivery Fee</span><span>₹{orderDetails?.order?.billSummary?.deliveryFee}</span></div>
                  {/* ⭐ Platform Fee, Packing Charge, Rain Fee and Round Off
                      are all baked into grandTotal server-side but were
                      missing here, so the visible rows never summed to the
                      Total shown below — see billSummary in
                      orders.controller.js for the full explanation. */}
                  {Number(orderDetails?.order?.billSummary?.platformFee) > 0 && (
                    <div className="flex justify-between"><span>Platform Fee</span><span>₹{orderDetails.order.billSummary.platformFee}</span></div>
                  )}
                  {Number(orderDetails?.order?.billSummary?.packingCharge) > 0 && (
                    <div className="flex justify-between"><span>Packing Charge</span><span>₹{orderDetails.order.billSummary.packingCharge}</span></div>
                  )}
                  {Number(orderDetails?.order?.billSummary?.rainFee) > 0 && (
                    <div className="flex justify-between"><span>Rain Fee</span><span>₹{orderDetails.order.billSummary.rainFee}</span></div>
                  )}
                  {Number(orderDetails?.order?.billSummary?.additionalChargesTotal) > 0 && (
                    <div className="flex justify-between"><span>Additional Charges</span><span>₹{orderDetails.order.billSummary.additionalChargesTotal}</span></div>
                  )}
                  <div className="flex justify-between"><span>Discount</span><span>-₹{orderDetails?.order?.billSummary?.discount}</span></div>
                  {Number(orderDetails?.order?.billSummary?.roundOff) !== 0 && (
                    <div className="flex justify-between">
                      <span>Round Off</span>
                      <span>{Number(orderDetails.order.billSummary.roundOff) > 0 ? '+' : '-'}₹{Math.abs(Number(orderDetails.order.billSummary.roundOff)).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg"><span>Total</span><span>₹{orderDetails?.order?.billSummary?.grandTotal}</span></div>
                </div>

                {/* MONEY FLOW + TRANSACTIONS */}
                {orderDetails?.order?.moneyFlow && (
                  <div
                    className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3"
                    data-testid="order-transactions-section"
                  >
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2">
                      <Wallet size={14} /> Money Flow
                    </h3>

                    {(() => {
                      const m = orderDetails.order.moneyFlow;
                      // Percentages are computed here from fields the popup
                      // already has, so they work even against an older
                      // backend that doesn't send commissionPercent yet.
                      const bs = orderDetails?.order?.billSummary || {};
                      const itemsTotal = Number(bs.itemTotal || 0);
                      const commissionAmt = Number(m.commissionAmount ?? bs.commissionAmount ?? 0);
                      const commissionPct = itemsTotal > 0
                        ? Number(((commissionAmt / itemsTotal) * 100).toFixed(2))
                        : 0;
                      const platformSharePct = Number(m.userPaid) > 0
                        ? Number(((Number(m.platformEarning || 0) / Number(m.userPaid)) * 100).toFixed(2))
                        : 0;
                      const tiles = [
                        {
                          k: 'userPaid',
                          label: 'User Paid',
                          sub: `${paymentMethodLabel(m.paymentMethod)} · ${m.paymentStatus || ''}`,
                          val: m.userPaid,
                          color: 'border-blue-200 bg-blue-50',
                          text: 'text-blue-800'
                        },
                        {
                          k: 'restaurantEarning',
                          label: 'Restaurant Gets',
                          sub: `${orderDetails?.order?.resturant?.name || ''}`,
                          val: m.restaurantEarning,
                          color: 'border-orange-200 bg-orange-50',
                          text: 'text-orange-800'
                        },
                        {
                          k: 'deliveryEarning',
                          label: 'Delivery Agent Gets',
                          sub: `${orderDetails?.order?.deliveryAgent?.name || 'Unassigned'}`,
                          val: m.deliveryEarning,
                          color: 'border-cyan-200 bg-cyan-50',
                          text: 'text-cyan-800'
                        },
                        {
                          k: 'commission',
                          label: 'Commission',
                          sub: `Order Amount ₹${formatINR(itemsTotal)}`,
                          pct: commissionPct,
                          val: commissionAmt,
                          color: 'border-indigo-200 bg-indigo-50',
                          text: 'text-indigo-800'
                        },
                        {
                          // Customer Payment − Restaurant Payment − Rider Payment
                          // (userPaid is already net of any discount, so this is
                          // the actual amount left over for the platform — not
                          // just the configured commission rate).
                          k: 'platformEarning',
                          label: 'Admin / Platform',
                          sub: `${formatINR(platformSharePct)}% of User Paid`,
                          pct: platformSharePct,
                          val: m.platformEarning,
                          color: 'border-purple-200 bg-purple-50',
                          text: 'text-purple-800'
                        }
                      ];
                      if (m.discount > 0) {
                        tiles.push({
                          k: 'discount',
                          label: 'Discount Given',
                          sub: 'Already netted out of User Paid',
                          val: m.discount,
                          color: 'border-rose-200 bg-rose-50',
                          text: 'text-rose-800'
                        });
                      }
                      if (m.codCollected > 0) {
                        tiles.push({
                          k: 'codCollected',
                          label: 'COD Collected',
                          sub: 'Cash from customer',
                          val: m.codCollected,
                          color: 'border-emerald-200 bg-emerald-50',
                          text: 'text-emerald-800'
                        });
                      }
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                          {tiles.map((t) => (
                            <div
                              key={t.k}
                              className={`rounded-md border ${t.color} p-2`}
                              data-testid={`money-tile-${t.k}`}
                            >
                              <p className={`text-[10px] font-semibold uppercase tracking-wide ${t.text}`}>
                                {t.label}
                              </p>
                              <p className={`text-base font-bold ${t.text} flex items-center gap-1.5`}>
                                ₹{formatINR(t.val)}
                                {t.pct != null && (
                                  <span
                                    className={`text-[11px] font-semibold rounded px-1.5 py-0.5 bg-white/70 border ${t.color}`}
                                    data-testid={`money-tile-${t.k}-pct`}
                                  >
                                    {formatINR(t.pct)}%
                                  </span>
                                )}
                              </p>
                              {t.sub && (
                                <p className="text-[10px] text-gray-600 truncate">{t.sub}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* RAW TRANSACTIONS */}
                    <h4 className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                      <Receipt size={12} /> Transactions ({orderDetails.order.transactions?.length || 0})
                    </h4>
                    {orderDetails.order.transactions?.length ? (
                      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full min-w-[560px] text-xs" data-testid="order-transactions-table">
                          <thead className="bg-slate-100 text-slate-600">
                            <tr>
                              <th className="text-left px-2 py-1.5 font-medium">Type</th>
                              <th className="text-left px-2 py-1.5 font-medium">Beneficiary</th>
                              <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                              <th className="text-left px-2 py-1.5 font-medium">Method</th>
                              <th className="text-left px-2 py-1.5 font-medium">Status</th>
                              <th className="text-left px-2 py-1.5 font-medium">Time</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {orderDetails.order.transactions.map((t) => {
                              const typeColor = {
                                ORDER_PAYMENT:      'bg-blue-100 text-blue-700',
                                RESTAURANT_EARNING: 'bg-orange-100 text-orange-700',
                                DELIVERY_EARNING:   'bg-cyan-100 text-cyan-700',
                                COD_COLLECTION:     'bg-emerald-100 text-emerald-700',
                                PLATFORM_EARNING:   'bg-purple-100 text-purple-700'
                              }[t.type] || 'bg-slate-100 text-slate-700';
                              const beneficiary = t.type === 'ORDER_PAYMENT'
                                ? `Customer → Platform`
                                : t.type === 'RESTAURANT_EARNING'
                                  ? (orderDetails?.order?.resturant?.name || 'Restaurant')
                                  : (t.type === 'DELIVERY_EARNING' || t.type === 'COD_COLLECTION')
                                    ? (orderDetails?.order?.deliveryAgent?.name || 'Delivery Agent')
                                    : '—';
                              return (
                                <tr key={t.id} data-testid={`order-txn-${t.id}`}>
                                  <td className="px-2 py-1.5">
                                    <Badge className={`${typeColor} text-[10px]`}>
                                      {t.type}
                                    </Badge>
                                  </td>
                                  <td className="px-2 py-1.5 text-gray-700 truncate max-w-[140px]" title={beneficiary}>
                                    {beneficiary}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                                    ₹{formatINR(t.amount)}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <Badge variant="outline" className="text-[10px]">{t.paymentMethod || '—'}</Badge>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <Badge className={`text-[10px] ${t.status === 'SUCCESS' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                      {t.status || '—'}
                                    </Badge>
                                  </td>
                                  <td className="px-2 py-1.5 text-[11px] text-gray-500 whitespace-nowrap">
                                    {t.createdAt ? format(new Date(t.createdAt), 'dd MMM, hh:mm a') : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No transactions recorded for this order yet.</p>
                    )}
                  </div>
                )}

                <Button className="mt-4 w-full" onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}>
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
